import { useTranslation } from "react-i18next";
import { Box, alpha, useTheme } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import type { ExplorerLivestreamProgramViewerShapePoint } from "../../services/api";
import {
  formatViewerIntegerFloor,
  LIVESTREAM_METRIC_LABELS,
} from "./utils";

export function formatProgramViewerShapeMinute(minute: number): string {
  const hours = Math.floor(minute / 60);
  const minutes = minute % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

export function shapeAxisTicks(maxMinute: number): number[] {
  if (maxMinute <= 0) return [0];
  const step = maxMinute >= 120 ? 30 : 15;
  const ticks: number[] = [];
  for (let minute = 0; minute <= maxMinute; minute += step) {
    ticks.push(minute);
  }
  if (ticks[ticks.length - 1] !== maxMinute) ticks.push(maxMinute);
  return ticks;
}

export type LivestreamProgramViewerShapeChartProps = {
  data: ExplorerLivestreamProgramViewerShapePoint[];
  color: string;
  height: number;
};

export const LivestreamProgramViewerShapeChart: React.FC<
  LivestreamProgramViewerShapeChartProps
> = ({ data, color, height }) => {
  const { t } = useTranslation("explorer");
  const theme = useTheme();
  const maxMinute = data[data.length - 1]?.minute ?? 0;

  return (
    <Box
      sx={{
        width: "100%",
        height,
        pl: 0.75,
        outline: "none",
        overflow: "visible",
        "& .recharts-responsive-container, & .recharts-wrapper, & .recharts-surface": {
          outline: "none",
          overflow: "visible",
        },
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={alpha(theme.palette.divider, 0.15)}
            vertical={false}
          />
          <XAxis
            type="number"
            dataKey="minute"
            domain={[0, maxMinute]}
            ticks={shapeAxisTicks(maxMinute)}
            tickFormatter={formatProgramViewerShapeMinute}
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            tickLine={false}
            axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
            tickLine={false}
            axisLine={false}
            width={52}
            allowDecimals={false}
            tickFormatter={(value) => formatViewerIntegerFloor(value as number)}
          />
          <RechartsTooltip
            cursor={{ stroke: alpha(theme.palette.divider, 0.4) }}
            formatter={(value) => [
              formatViewerIntegerFloor(value as number),
              LIVESTREAM_METRIC_LABELS.avgEsp,
            ]}
            labelFormatter={(label, payload) => {
              const point = payload?.[0]?.payload as
                | ExplorerLivestreamProgramViewerShapePoint
                | undefined;
              const minute =
                typeof label === "number" ? label : Number(point?.minute ?? label);
              return `${formatProgramViewerShapeMinute(
                Number.isFinite(minute) ? minute : 0,
              )} · ${t("programViewerShapeEpisodes", {
                count: point?.episodeCount ?? 0,
              })}`;
            }}
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 8,
              padding: "6px 10px",
              fontSize: 12,
            }}
          />
          <Line
            type="monotone"
            dataKey="avgViewers"
            name={LIVESTREAM_METRIC_LABELS.avgEsp}
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: color }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
